import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@sunstone/ui';
import { CheckCircle2, Globe, ShieldCheck, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuthToken } from '@/features/auth';
import { useAppSelector } from '@/store';

interface StatusResponse {
  status: string;
  message?: string;
  authenticated_user?: string;
  stats?: {
    database?: { status: string; latency_us: number };
    temporal?: { status: string; latency_us: number };
  };
}

function StatusIndicator({ status }: { status: 'ok' | 'error' | 'loading' }) {
  if (status === 'ok') {
    return (
      <Badge className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300 gap-1">
        <CheckCircle2 className="w-3 h-3" />
        Healthy
      </Badge>
    );
  }
  if (status === 'error') {
    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="w-3 h-3" />
        Error
      </Badge>
    );
  }
  return <Badge>Checking…</Badge>;
}

export function StatusCheck() {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const { token, tokenError } = useAuthToken();
  const [publicStatus, setPublicStatus] = useState<StatusResponse | null>(null);
  const [authStatus, setAuthStatus] = useState<StatusResponse | null>(null);
  const [publicError, setPublicError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

  useEffect(() => {
    const checkPublicStatus = async () => {
      try {
        const response = await fetch(`${apiUrl}/readiness.v1.StatusService/Check`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Connect-Protocol-Version': '1',
          },
          body: JSON.stringify({}),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setPublicStatus(data);
        setPublicError(null);
      } catch (error) {
        setPublicError(error instanceof Error ? error.message : 'Unknown error');
        setPublicStatus(null);
      } finally {
        setLoading(false);
      }
    };

    checkPublicStatus();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setAuthStatus(null);
      setAuthError(null);
      return;
    }

    const checkAuthStatus = async () => {
      try {
        const response = await fetch(`${apiUrl}/readiness.v1.AuthStatusService/CheckAuth`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Connect-Protocol-Version': '1',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        setAuthStatus(data);
        setAuthError(null);
      } catch (error) {
        setAuthError(error instanceof Error ? error.message : 'Unknown error');
        setAuthStatus(null);
      }
    };

    checkAuthStatus();
  }, [isAuthenticated, token]);

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {/* Public API Card */}
      <Card className="min-h-[200px]">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              Public API
            </span>
            {loading ? (
              <Skeleton className="h-5 w-16" />
            ) : (
              <StatusIndicator status={publicError ? 'error' : 'ok'} />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm">
            <span className="font-medium text-foreground">Endpoint</span>
            <code className="mt-1 block text-xs bg-muted px-2 py-1 rounded text-muted-foreground">
              /readiness.v1.StatusService/Check
            </code>
          </div>

          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : publicError ? (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertTitle>Connection failed</AlertTitle>
              <AlertDescription>
                <p>{publicError}</p>
                <p className="mt-1">
                  Ensure the backend is running on{' '}
                  <code className="bg-muted px-1 rounded">{apiUrl}</code>
                </p>
              </AlertDescription>
            </Alert>
          ) : publicStatus ? (
            <div className="space-y-2">
              {publicStatus.message && (
                <p className="text-xs text-muted-foreground">{publicStatus.message}</p>
              )}
              {publicStatus.stats && (
                <div className="grid grid-cols-2 gap-2">
                  {publicStatus.stats.database && (
                    <div className="rounded-md bg-muted/50 p-2">
                      <p className="text-xs font-medium">Database</p>
                      <p className="text-xs text-muted-foreground">
                        {publicStatus.stats.database.status} ·{' '}
                        {publicStatus.stats.database.latency_us
                          ? (publicStatus.stats.database.latency_us / 1000).toFixed(1)
                          : '0.0'}
                        ms
                      </p>
                    </div>
                  )}
                  {publicStatus.stats.temporal && (
                    <div className="rounded-md bg-muted/50 p-2">
                      <p className="text-xs font-medium">Temporal</p>
                      <p className="text-xs text-muted-foreground">
                        {publicStatus.stats.temporal.status} ·{' '}
                        {publicStatus.stats.temporal.latency_us
                          ? (publicStatus.stats.temporal.latency_us / 1000).toFixed(1)
                          : '0.0'}
                        ms
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Auth API Card */}
      <Card className="min-h-[200px]">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-muted-foreground" />
              Authenticated API
            </span>
            {!isAuthenticated ? (
              <Badge>Not signed in</Badge>
            ) : loading || (!token && !tokenError) ? (
              <Skeleton className="h-5 w-16" />
            ) : (
              <StatusIndicator
                status={authError || tokenError ? 'error' : authStatus ? 'ok' : 'loading'}
              />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm">
            <span className="font-medium text-foreground">Endpoint</span>
            <code className="mt-1 block text-xs bg-muted px-2 py-1 rounded text-muted-foreground">
              /readiness.v1.AuthStatusService/CheckAuth
            </code>
          </div>

          {!isAuthenticated ? (
            <p className="text-sm text-muted-foreground">Sign in to check authenticated status.</p>
          ) : tokenError ? (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertTitle>Token retrieval failed</AlertTitle>
              <AlertDescription>{tokenError.message}</AlertDescription>
            </Alert>
          ) : !token ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : authError ? (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertTitle>Authentication failed</AlertTitle>
              <AlertDescription>{authError}</AlertDescription>
            </Alert>
          ) : authStatus ? (
            <div className="space-y-2">
              {authStatus.message && (
                <p className="text-xs text-muted-foreground">{authStatus.message}</p>
              )}
              {authStatus.authenticated_user && (
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="text-xs font-medium">Authenticated User</p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {authStatus.authenticated_user}
                  </p>
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
